class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Outside-click close. Bound at the document level in the capture phase (added on
    // open, removed on close) rather than on the overlay, because:
    //   1) #CartDrawer's innerHTML — including the overlay — is replaced on every cart
    //      refresh, which would orphan a listener bound to the overlay element; and
    //   2) third-party scripts (e.g. Klaviyo) can layer a viewport-covering element above
    //      the overlay and swallow the click. Capture phase on document fires first
    //      regardless of what is stacked on top.
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.setHeaderCartIconAccessibility();
  }

  onDocumentClick(event) {
    if (!this.classList.contains('active')) return;
    const target = event.target;
    // Keep clicks inside the panel, and on the cart icon (which toggles open).
    if (target.closest?.('.drawer__inner')) return;
    if (target.closest?.('#cart-icon-bubble')) return;
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // The green "added to cart" notice is only relevant right after an add.
    // Hide it on every open(); the add flow calls showAddedNotice() afterwards.
    this.hideAddedNotice();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // Added here (next tick) so the click that opened the drawer doesn't immediately close it.
      document.addEventListener('click', this.onDocumentClick, true);
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.removeEventListener('click', this.onDocumentClick, true);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // An add always leaves the cart non-empty. Clear `is-empty` from the
    // <cart-drawer> element itself (not just .drawer__inner): the CSS rule
    // `.is-empty .cart__contents { display: none }` would otherwise hide the
    // line items we're about to inject until the next page reload.
    this.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Re-hydrate the footer's dynamic-checkout wallets — Shopify doesn't auto-init
    // section-rendered accelerated-checkout buttons (see refresh() / cross-sell-popup).
    window?.Shopify?.PaymentButton?.init();

    setTimeout(() => {
      // Outside-click close is handled by the delegated listener on <cart-drawer>
      // (see constructor), so no per-overlay re-binding is needed after a refresh.
      this.open();
      this.showAddedNotice(parsedState.product_title);
    });
  }

  // Green "added to cart" notice (Figma node 611:46). `title` is the just-added
  // product title (from the /cart/add response). No title → leave it hidden.
  showAddedNotice(title) {
    const notice = this.querySelector('.cart-drawer__added');
    if (!notice || !title) return;
    const text = notice.querySelector('.cart-drawer__added-text');
    const template = notice.dataset.addedTemplate || '[title]';
    if (text) text.textContent = template.replace('[title]', title);
    notice.hidden = false;
  }

  hideAddedNotice() {
    const notice = this.querySelector('.cart-drawer__added');
    if (notice) notice.hidden = true;
  }

  // Re-render the whole drawer (#CartDrawer: header, items, recommendations, footer)
  // and the cart bubble from the server, then sync the empty-state class. Used by the
  // recommendations cards after an add. Replacing #CartDrawer's HTML re-inserts the
  // self-initializing <cart-reco-card> / quantity inputs, so listeners stay live.
  async refresh() {
    try {
      const sections = this.getSectionsToRender().map((s) => s.id);
      // Fetch from the current page path (not /cart) so the footer's dynamic-checkout
      // wallets — gated to `request.page_type != 'cart'` — are included on re-render.
      // Fetching /cart would set page_type == 'cart' and strip them. See cross-sell-popup.js.
      const base = window.location.pathname || routes.cart_url;
      const url = `${base}?sections=${encodeURIComponent(sections.join(','))}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      const data = await res.json();
      this.getSectionsToRender().forEach((section) => {
        const target = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        const html = data[section.id];
        if (!target || !html) return;
        const el = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector(section.selector || '.shopify-section');
        if (el) target.innerHTML = el.innerHTML;
      });
      const drawerHtml = data['cart-drawer'];
      if (drawerHtml) {
        const fresh = new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('cart-drawer');
        if (fresh) this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      }
      // Re-hydrate the footer's dynamic-checkout wallets (not auto-inited when injected).
      window?.Shopify?.PaymentButton?.init();
    } catch (e) {
      console.error('CartDrawer.refresh failed', e);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
